import { Switch, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import { BankingPartner } from '../../types/user.types'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { parseNumber } from './utils'

type BankingPartnerAdditionalData = {
  accountId: string
  bankingPartnerId: string
  bankingPartnerName: string
}

export type CustomerCompanyFeesData = {
  fee: string
  currency: string
  allowedToOperate: boolean
  needsAdditionalData: boolean
  bankingPartnerIds: Array<string>
  additionalData: Array<BankingPartnerAdditionalData>
}

export const NewCustomerCompanyFormBankingPartners = forwardRef(
  ({ errors, availableCurrencies, currentBankingPartners }: { errors: { [key: string]: string }; availableCurrencies: Array<string>; currentBankingPartners: Array<BankingPartner> }, ref) => {
    const { t } = useTranslation(['customer-companies-management'])

    const [customerCompanyBankingPartnersData, setCustomerCompanyBankingPartnersData] = useState<Array<CustomerCompanyFeesData>>([])

    useImperativeHandle(
      ref,
      () => {
        return {
          customerCompanyBankingPartnersData,
          handleSetBankingPartnersData: (bankingPartnersDataToSet: Array<CustomerCompanyFeesData>) => {
            setCustomerCompanyBankingPartnersData(bankingPartnersDataToSet)
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [customerCompanyBankingPartnersData]
    )

    useEffect(() => {
      const customerCompanyBankingPartnersDataToSet = []

      for (const availableCurrency of availableCurrencies) {
        const additionalDataToSet = []
        const bankingPartnersByCurrency = currentBankingPartners.filter((bankingPartner) => bankingPartner.currency === availableCurrency)
        const bankingPartnerWithAdditionalData = currentBankingPartners.filter((bankingPartner) => bankingPartner.currency === availableCurrency && bankingPartner.needsAdditionalData === true)

        const bankingPartnersByCurrencyIds = bankingPartnersByCurrency.map((bankingPartner) => String(bankingPartner._id))

        for (const additionalData of bankingPartnerWithAdditionalData) {
          additionalDataToSet.push({
            accountId: '',
            bankingPartnerId: String(additionalData._id),
            bankingPartnerName: additionalData.name.toUpperCase()
          })
        }

        customerCompanyBankingPartnersDataToSet.push({
          fee: '$ 0',
          allowedToOperate: true,
          currency: availableCurrency,
          additionalData: additionalDataToSet,
          bankingPartnerIds: bankingPartnersByCurrencyIds,
          needsAdditionalData: bankingPartnerWithAdditionalData.length > 0
        })
      }

      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersDataToSet)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleGetFeeQuantity = (valueToSet: string) => {
      const extractedNumberOfValueToSet = valueToSet.replaceAll('$', '').replaceAll(',', '').trim()

      if (isNaN(Number(extractedNumberOfValueToSet)) && extractedNumberOfValueToSet.slice(-1) !== '.') return ''

      if (extractedNumberOfValueToSet === '') return ''

      return parseNumber(extractedNumberOfValueToSet)
    }

    const handleChangeFeesData = (feeToSet: string, indexOfFee: number) => {
      const feeQuantityToSet = handleGetFeeQuantity(String(feeToSet))
      const customerCompanyBankingPartnersUpdatedData = [...customerCompanyBankingPartnersData]

      if (feeQuantityToSet === '') {
        customerCompanyBankingPartnersUpdatedData[indexOfFee].fee = feeQuantityToSet

        setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
        return
      }

      customerCompanyBankingPartnersUpdatedData[indexOfFee].fee = `$ ${feeQuantityToSet}`
      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
    }

    const handleClearFeeValue = (indexOfFee: number) => {
      const customerCompanyBankingPartnersUpdatedData = [...customerCompanyBankingPartnersData]
      customerCompanyBankingPartnersUpdatedData[indexOfFee].fee = customerCompanyBankingPartnersUpdatedData[indexOfFee].fee.slice(0, -1)
      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
    }

    const handleSetFeeZero = (indexOfFee: number) => {
      const customerCompanyBankingPartnersUpdatedData = [...customerCompanyBankingPartnersData]
      customerCompanyBankingPartnersUpdatedData[indexOfFee].fee = '$ 0'
      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
    }

    const handleChangeFeesAdditionalData = (additionalDataToSet: string, indexOfFee: number, additionalIndexOfFee: number) => {
      const customerCompanyBankingPartnersUpdatedData = [...customerCompanyBankingPartnersData]
      customerCompanyBankingPartnersUpdatedData[indexOfFee].additionalData[additionalIndexOfFee].accountId = additionalDataToSet
      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
    }

    const handleChangeAllowedToOperate = (indexOfFee: number) => {
      const customerCompanyBankingPartnersUpdatedData = [...customerCompanyBankingPartnersData]
      customerCompanyBankingPartnersUpdatedData[indexOfFee].allowedToOperate = !customerCompanyBankingPartnersUpdatedData[indexOfFee].allowedToOperate
      setCustomerCompanyBankingPartnersData(customerCompanyBankingPartnersUpdatedData)
    }

    return (
      <div
        className="form-container"
        style={{ marginTop: '0px' }}
      >
        <div className="form-main-instruction">{t('newClientForm.formBankingPartners.mainInstruction')}</div>
        <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', padding: '0px 15px', margin: '24px 0px', gap: '12px' }}>
          {customerCompanyBankingPartnersData.map((customerCompanyBankingPartnerData: CustomerCompanyFeesData, index: number) => {
            return (
              <div
                key={customerCompanyBankingPartnerData.currency}
                style={{
                  gap: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: '12px',
                  flexDirection: 'column',
                  borderBottom: '1px solid #E5E5E5'
                }}
              >
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40%' }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      sx={textFieldWhiteBg}
                      error={!!errors[`feeError-${index}`]}
                      helperText={errors[`feeError-${index}`]}
                      value={customerCompanyBankingPartnerData.fee}
                      onChange={(event) => handleChangeFeesData(event.target.value, index)}
                      onBlur={() => {
                        if (customerCompanyBankingPartnerData.fee.endsWith('.')) {
                          handleClearFeeValue(index)
                        }

                        if (customerCompanyBankingPartnerData.fee === '') {
                          handleSetFeeZero(index)
                        }
                      }}
                      label={`Fee ${customerCompanyBankingPartnerData.currency.toUpperCase()}`}
                    />
                  </div>
                  <div style={{ width: '10%', color: '#737373', textAlign: 'center', fontFamily: "'Open Sans', sans-serif", fontSize: '16px', fontStyle: 'normal', fontWeight: 400, lineHeight: '150%' }}>
                    {customerCompanyBankingPartnerData.currency.toUpperCase()}
                  </div>
                  <div style={{ width: '40%', display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '12px' }}>
                      <Switch
                        disabled={customerCompanyBankingPartnerData.currency.toUpperCase() === 'COP'}
                        checked={customerCompanyBankingPartnerData.allowedToOperate}
                        sx={{ '& .Mui-checked .MuiSwitch-thumb': { backgroundColor: '#107569' }, '& .Mui-checked .MuiSwitch-track': { backgroundColor: 'rgb(46, 211, 183) !important' } }}
                        onChange={() => {
                          handleChangeAllowedToOperate(index)
                        }}
                      />
                      <div style={{ color: '#737373', fontFamily: "'Open Sans', sans-serif", fontSize: '14px', fontWeight: 400, lineHeight: '150%' }}>{t('newClientForm.formBankingPartners.allowToOperate')}</div>
                    </div>
                    {errors[`checkError-${index}`] && (
                      <div>
                        <div
                          className="error-message"
                          style={{ marginTop: '0px' }}
                        >
                          {errors[`checkError-${index}`]}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {customerCompanyBankingPartnerData.needsAdditionalData === true && (
                  <>
                    {customerCompanyBankingPartnerData.additionalData.map((additionalData: BankingPartnerAdditionalData, additionalIndex: number) => {
                      return (
                        <TextField
                          fullWidth
                          variant="standard"
                          sx={textFieldWhiteBg}
                          value={additionalData.accountId}
                          key={additionalData.bankingPartnerId}
                          error={!!errors[`additionalData-${index}-${additionalIndex}`]}
                          helperText={errors[`additionalData-${index}-${additionalIndex}`]}
                          label={`Account id ${additionalData.bankingPartnerName.toUpperCase()}`}
                          onChange={(event) => handleChangeFeesAdditionalData(event.target.value, index, additionalIndex)}
                        />
                      )
                    })}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
)
